/**
 * Renders the "Additional Info" block on an employee profile.
 *
 * Active custom fields are grouped by category. If ``canEditAll`` is
 * true (HR admin viewing anyone), every field is writable. If false
 * (employee viewing themselves), only fields where
 * ``is_editable_by_employee`` is true appear editable; others render
 * read-only. "Save changes" does a single bulk PUT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchEmployeeValues,
  updateEmployeeValues,
  type CustomFieldValueItem,
} from "@/api/customFields";
import { useToast } from "@/hooks/useToast";

import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

interface Props {
  employeeId: string;
  canEditAll: boolean;
}

export function CustomFieldsPanel({ employeeId, canEditAll }: Props) {
  const { addToast } = useToast();
  const [items, setItems] = useState<CustomFieldValueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchEmployeeValues(employeeId);
      setItems(res.values);
      setDirty({});
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't load additional info: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId, addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const m = new Map<string, CustomFieldValueItem[]>();
    for (const v of items) {
      const arr = m.get(v.category) ?? [];
      arr.push(v);
      m.set(v.category, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const setFieldValue = (field_id: string, v: unknown) => {
    setDirty((prev) => ({ ...prev, [field_id]: v }));
  };

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const res = await updateEmployeeValues(employeeId, dirty);
      setItems(res.values);
      setDirty({});
      addToast("success", "Additional info saved");
    } catch (e: any) {
      addToast(
        "error",
        `Save failed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card className="text-sm text-text-muted">Loading…</Card>;
  }
  if (items.length === 0) {
    return (
      <Card className="text-sm text-text-muted">
        No custom fields defined for your organization yet.
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(([category, rows]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="capitalize">{category}</CardTitle>
          </CardHeader>
          <div className="space-y-4">
            {rows.map((item) => {
              const editable = canEditAll || item.is_editable_by_employee;
              const draft =
                item.field_id in dirty ? dirty[item.field_id] : item.parsed_value;
              return (
                <FieldRenderer
                  key={item.field_id}
                  item={item}
                  editable={editable}
                  value={draft as string | number | boolean | null}
                  onChange={(v) => setFieldValue(item.field_id, v)}
                />
              );
            })}
          </div>
        </Card>
      ))}
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving || Object.keys(dirty).length === 0}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function FieldRenderer({
  item,
  editable,
  value,
  onChange,
}: {
  item: CustomFieldValueItem;
  editable: boolean;
  value: string | number | boolean | null;
  onChange: (v: unknown) => void;
}) {
  const label = `${item.field_name}${item.is_required ? " *" : ""}`;
  if (!editable) {
    return (
      <div>
        <p className="text-xs text-text-muted">{label}</p>
        <p className="text-sm">{formatReadOnly(item)}</p>
      </div>
    );
  }
  if (item.field_type === "textarea") {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        <textarea
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          rows={3}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  if (item.field_type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
    );
  }
  if (item.field_type === "dropdown") {
    return (
      <Select
        label={label}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: "", label: "—" },
          ...(item.options ?? []).map((o) => ({ value: o, label: o })),
        ]}
      />
    );
  }
  const inputType =
    item.field_type === "number"
      ? "number"
      : item.field_type === "date"
        ? "date"
        : item.field_type === "email"
          ? "email"
          : item.field_type === "phone"
            ? "tel"
            : "text";
  return (
    <Input
      label={label}
      type={inputType}
      value={value == null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function formatReadOnly(item: CustomFieldValueItem): string {
  const v = item.parsed_value;
  if (v === null || v === undefined || v === "") return "—";
  if (item.field_type === "checkbox") return v ? "Yes" : "No";
  return String(v);
}
